import { AlertMessage, Theme, useNextgenRouter, useTranslation } from '@nextgen-web-framework/all';
import {
  type Cart,
  CartConstantsEnum,
  type CartEntryDetails,
  CartEventsEnum,
  cartApi,
  getActiveCart,
  updateCart,
  isDeliveryItemUnavailable,
  isPickUpItemUnavailable,
  isThirdPartyPickUpUnavailable,
  Frequency,
  isExceptionExist,
  ExceptionsEnum,
  CartEvents,
} from '@commerce-cart-webcomponents/sdk';
import type { AppDispatch } from '@nextgen-web-framework/core';
import { StorageService } from '@nextgen-web-framework/core';
import { useDispatch } from 'react-redux';
import { ProductFlag } from '../product-flag/ProductFlag';
import styles from './CartLineItemErrorMessages.module.scss';

export type CartLineItemErrorMessagesProps = {
  data: Cart;
  entry: CartEntryDetails;
  removeRationingItemCB: (entryId: number) => void;
  bottomMsgPresent?: boolean;
  hasShippingError?: boolean;
};
const CartLineItemErrorMessages = ({
  data,
  entry,
  removeRationingItemCB,
  bottomMsgPresent,
  hasShippingError,
}: CartLineItemErrorMessagesProps) => {
  const { rationingResult } = entry;
  const availableQty = rationingResult?.[0]?.availableQuantity;
  const t = useTranslation();

  const router = useNextgenRouter();
  const dispatch = useDispatch<AppDispatch>();

  const cartId = router.params?.cartId ?? '';
  const mainSubCart = getActiveCart(data, cartId as string);

  const showAlertMsg = (!availableQty || entry.quantity > availableQty) && availableQty !== undefined;

  const updateRationingItemCB = (entry: CartEntryDetails) => {
    // cart count will be available quantity for that product
    const payload = {
      entry: {
        entryId: entry.entryId,
        product: {
          id: entry.product.id,
        },
        quantity: entry.rationingResult?.[0]?.availableQuantity as number,
        bundleSlots: entry.bundleSlots,
      },
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
    };
    cartApi
      .changeQtyForEntryFromCart({
        cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        subCartId: mainSubCart?.subCartId,
        entryId: entry.entryId,
        payload,
      })
      .then((res) => {
        dispatch(updateCart(res as Cart));
        CartEvents.publishEvent({
          event: CartEventsEnum.UpdateCartEntryQty,
          payload: {
            cart: res,
            cartId: (res as Cart).id,
          },
        });
      });
  };

  const frequency = rationingResult?.[0]?.frequency;

  const rationingHeading = t('cart.cartLineItem.remainingLimit')
    .replace(/{{frequency}}/g, frequency === Frequency.Lifetime ? '' : t(`cart.cartLineItem.frequency.${frequency}`))
    .replace(/{{availableQuantity}}/g, String(availableQty));

  const showAlertErrorMsg = (heading: string, textLinkText: string, onTextLinkClick?: () => void) => (
    <div className={`${styles['lineItem-bottom-msg']} ${bottomMsgPresent && styles['adjust-padding']}`}>
      <div className={styles['cart-exceptionAlert']}>
        <AlertMessage
          theme={Theme.ERROR}
          icon={'exclamation-square'}
          content={{
            heading: heading,
          }}
          textLink={{
            text: textLinkText,
            onClick: onTextLinkClick,
          }}
        />
      </div>
    </div>
  );

  return (
    <>
      {showAlertMsg &&
        !hasShippingError &&
        showAlertErrorMsg(
          rationingHeading,
          availableQty ? t('cart.errorMsgs.updateRationingItem') : t('cart.errorMsgs.removeRationingItem'),
          () => {
            availableQty ? updateRationingItemCB(entry) : removeRationingItemCB(entry.entryId as number);
          },
        )}
      {isPickUpItemUnavailable(true, {}, entry) &&
        showAlertErrorMsg(t('cart.cartLineItemsHeader.noPickUp'), t('cart.cartLineItemErrorMsgs.remove'), () => {
          removeRationingItemCB(entry.entryId as number);
        })}

      {isDeliveryItemUnavailable(true, {}, entry) &&
        showAlertErrorMsg(t('cart.cartLineItemsHeader.noHomedelivery'), t('cart.cartLineItemErrorMsgs.remove'), () => {
          removeRationingItemCB(entry.entryId as number);
        })}

      {isThirdPartyPickUpUnavailable(true, {}, entry) &&
        showAlertErrorMsg(
          t('cart.cartLineItemsHeader.no3rdPartyPickup'),
          t('cart.cartLineItemErrorMsgs.remove'),
          () => {
            removeRationingItemCB(entry.entryId as number);
          },
        )}
      {isExceptionExist(ExceptionsEnum.Exceptions.Restricted, entry.errors ?? []) &&
        showAlertErrorMsg(t('cart.errorMessages.currentlyUnavailble'), t('cart.cartLineItemErrorMsgs.remove'), () => {
          removeRationingItemCB(entry.entryId as number);
        })}
      {isExceptionExist(ExceptionsEnum.Exceptions.CartLevelPromotion, entry.errors ?? []) && (
        <ProductFlag iconName="tags" text={t('cart.errorMessages.cartPromotions')} theme={Theme.GRAY} />
      )}
    </>
  );
};

export { CartLineItemErrorMessages };
